/**
 * projectRollups.js — project- and batch-level rollup aggregations.
 *
 * Updated 2026-04-20 for the 4-state batch lifecycle (backbone Phase 1a).
 * Delegates item-counting to `batchProgress.js` — the canonical rule engine
 * defined by _NORTH-STAR.md §5.3.
 *
 * This file contains:
 *   - CLIENT_VISIBLE_BATCH_STATUSES  — the 3 batch states a client portal shows
 *   - ACTIVE_PROJECT_STATUSES        — the 3 job statuses that count as "active"
 *   - Quality index builders          — state-agnostic quality aggregation
 *   - computeBatchRollup              — per-job batch aggregate (delegates to batchProgress)
 *   - computeProjectPortfolioRollup   — portfolio-level project aggregate
 */

import {
  BATCH_STATES,
  sumTotalItems,
  sumCreditedItems,
  sumBillableItems,
  sumAcceptedItems,
  sumRejectedItems,
} from './batchProgress';

// ─── Constants ───────────────────────────────────────────────────────────

/**
 * Batch states the client can see in their portal. Per _NORTH-STAR.md §5.2,
 * clients see work once it has moved past In Progress (internal-only).
 *
 *   In Review  — SPL/QA is reviewing; client sees it as "in pipeline"
 *   Delivered  — handed to client; awaiting their acceptance
 *   Accepted   — client signed off; terminal
 */
export const CLIENT_VISIBLE_BATCH_STATUSES = [
  BATCH_STATES.IN_REVIEW,
  BATCH_STATES.DELIVERED,
  BATCH_STATES.ACCEPTED,
];

/**
 * Job/project statuses that count as active (for portfolio rollups, dashboards).
 * These are job.status values — distinct from batch.status, not affected by the
 * backbone 4-state lifecycle collapse.
 */
export const ACTIVE_PROJECT_STATUSES = ['Open', 'Active', 'Closing Soon'];

// ─── Internal helpers ────────────────────────────────────────────────────

function numberOrNull(value) {
  // Explicit null/undefined check before Number() — Number(null) === 0, which
  // would otherwise let nulls leak through as zeros into aggregations.
  if (value == null) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function averageScores(scores = []) {
  if (scores.length === 0) return null;
  return scores.reduce((sum, score) => sum + score, 0) / scores.length;
}

// ─── Quality indexes (state-agnostic) ────────────────────────────────────

/**
 * Compose the quality index key for an assignment (jobId + individualId).
 * Returns null when either is missing — useful for guards in callers.
 */
export function getAssignmentQualityKey(jobId, individualId) {
  if (!jobId || !individualId) return null;
  return `${jobId}::${individualId}`;
}

/**
 * Build two lookup maps from a list of quality_log rows:
 *   qualityByAssignment: Map<"jobId::individualId", number[]>
 *   qualityByIndividual: Map<individualId, number[]>
 *
 * Only positive accuracy_percent scores are included (null / 0 / negative skipped).
 */
export function buildQualityIndexes(qualityLogs = []) {
  const qualityByAssignment = new Map();
  const qualityByIndividual = new Map();

  (qualityLogs || []).forEach((log) => {
    const score = numberOrNull(log?.accuracy_percent);
    const individualId = log?.individual_id;
    if (!individualId || score == null || score <= 0) return;

    const assignmentKey = getAssignmentQualityKey(log?.job_id, individualId);
    if (assignmentKey) {
      const assignmentScores = qualityByAssignment.get(assignmentKey) || [];
      assignmentScores.push(score);
      qualityByAssignment.set(assignmentKey, assignmentScores);
    }

    const individualScores = qualityByIndividual.get(individualId) || [];
    individualScores.push(score);
    qualityByIndividual.set(individualId, individualScores);
  });

  return { qualityByAssignment, qualityByIndividual };
}

/**
 * Look up a derived quality score for an assignment, with cascading fallback:
 *   1. Assignment-specific scores (jobId + individualId)
 *   2. Individual-wide scores (across all their assignments)
 *   3. Caller-supplied fallbackQuality
 *   4. null
 */
export function getDerivedAssignmentQuality({
  jobId = null,
  individualId = null,
  fallbackQuality = null,
  qualityIndexes = null,
} = {}) {
  const assignmentKey = getAssignmentQualityKey(jobId, individualId);
  const assignmentScores = assignmentKey ? qualityIndexes?.qualityByAssignment?.get(assignmentKey) : null;
  if (assignmentScores?.length) {
    return averageScores(assignmentScores);
  }

  const individualScores = individualId ? qualityIndexes?.qualityByIndividual?.get(individualId) : null;
  if (individualScores?.length) {
    return averageScores(individualScores);
  }

  return numberOrNull(fallbackQuality);
}

/**
 * Average review quality from explicit quality_log rows. Falls back to
 * batch-level qa_scores if no quality_log data available.
 */
export function computeAverageReviewQuality({ qualityLogs = [], batchQaScores = [] } = {}) {
  const reviewScores = (qualityLogs || [])
    .map(log => numberOrNull(log?.accuracy_percent))
    .filter(score => score != null && score > 0);

  if (reviewScores.length > 0) {
    return averageScores(reviewScores);
  }

  const fallbackBatchScores = (batchQaScores || [])
    .map(score => numberOrNull(score))
    .filter(score => score != null && score > 0);

  if (fallbackBatchScores.length > 0) {
    return averageScores(fallbackBatchScores);
  }

  return null;
}

// ─── Batch rollup (delegates to batchProgress) ───────────────────────────

/**
 * Aggregate a set of batches into the fields a dashboard / report / client view
 * typically needs. Item counts are all sourced from `batchProgress.js` — the
 * canonical rule engine. Callers get:
 *
 *   totalItems       — sum of batch.total_items
 *   completedItems   — raw sum of items_completed (kept for deprecation back-compat)
 *   creditedItems    — progress (items_completed - items_rejected) per batchProgress
 *   billableItems    — accrual revenue (Delivered + Accepted) per batchProgress
 *   acceptedItems    — client-confirmed (Accepted only) per batchProgress
 *   rejectedItems    — rejection count
 *   batchCount       — non-null entries in the input
 *   visibleBatches   — entries whose status ∈ visibleStatuses (default: CLIENT_VISIBLE_BATCH_STATUSES)
 *   batchQaScores    — non-null qa_score values for averaging
 *   completionPercent — round(creditedItems / totalItems * 100)
 *                       Uses CREDITED, not raw completed, because rejected items
 *                       should NOT inflate progress (North Star invariant).
 *
 * @param {Array} batches
 * @param {Object} options
 * @param {string[]} [options.visibleStatuses] — override for visibleBatches count
 */
export function computeBatchRollup(batches = [], options = {}) {
  const visibleStatuses = options.visibleStatuses || CLIENT_VISIBLE_BATCH_STATUSES;
  const list = Array.isArray(batches) ? batches : [];

  const totalItems = sumTotalItems(list);
  const creditedItems = sumCreditedItems(list);
  const billableItems = sumBillableItems(list);
  const acceptedItems = sumAcceptedItems(list);
  const rejectedItems = sumRejectedItems(list);

  // Legacy: raw items_completed sum. Preserved so callers that specifically
  // want "claimed" count (pre-rejection accounting) can still access it. New
  // consumers should prefer `creditedItems`.
  const completedItems = list.reduce((s, b) => s + (Number(b?.items_completed) || 0), 0);

  let batchCount = 0;
  let visibleBatches = 0;
  const batchQaScores = [];

  for (const batch of list) {
    if (!batch) continue;
    batchCount += 1;
    if (visibleStatuses.includes(batch.status)) visibleBatches += 1;
    const qa = numberOrNull(batch.qa_score);
    if (qa != null) batchQaScores.push(qa);
  }

  return {
    totalItems,
    completedItems,   // legacy / back-compat
    creditedItems,    // progress
    billableItems,    // accrual revenue
    acceptedItems,    // cash-conservative
    rejectedItems,
    batchCount,
    visibleBatches,
    batchQaScores,
    completionPercent: totalItems > 0 ? Math.round((creditedItems / totalItems) * 100) : 0,
  };
}

// ─── Project-level rollup ────────────────────────────────────────────────

/**
 * Is this job status an "active" project? Used by portfolio views + filters.
 */
export function isActiveProjectStatus(status) {
  return ACTIVE_PROJECT_STATUSES.includes(status);
}

/**
 * Portfolio-level aggregate across a set of projects.
 */
export function computeProjectPortfolioRollup(projects = []) {
  const list = Array.isArray(projects) ? projects : [];
  let qualitySum = 0;
  let qualityCount = 0;

  const base = list.reduce((acc, project) => {
    acc.totalProjects += 1;
    if (isActiveProjectStatus(project?.status)) acc.activeProjects += 1;
    acc.totalItems += Number(project?.totalItems || 0);
    acc.completedItems += Number(project?.completedItems || 0);
    if (project?.avgQuality != null && project.avgQuality > 0) {
      qualitySum += project.avgQuality;
      qualityCount += 1;
    }
    return acc;
  }, { totalProjects: 0, activeProjects: 0, totalItems: 0, completedItems: 0 });

  base.completionPercent = base.totalItems > 0
    ? Math.round((base.completedItems / base.totalItems) * 100)
    : 0;
  base.avgQuality = qualityCount > 0
    ? Math.round((qualitySum / qualityCount) * 10) / 10
    : 0;
  return base;
}
